import { hierarchy, treemap, treemapSquarify } from 'd3-hierarchy';
import {
  type PropsWithChildren,
  forwardRef,
  useMemo,
  type JSX,
  type RefObject,
} from 'react';

import { Canvas } from '../../core/components/canvas/Canvas.js';
import { CanvasContext } from '../../core/components/canvas/CanvasContext.js';
import { CanvasShape } from '../../core/components/canvas/types/canvas-shape.js';
import { useGraphSize } from '../../core/components/chart-layout/hooks/useGraphSize.js';
import { TREEMAP_CHART_ARIA_LABEL } from '../constants.js';
import { useLabelsDisplay } from '../context/LabelsDisplayContext.js';
import { useTreeMapAggregationData } from '../context/TreeMapAggregationDataContext.js';
import { useTreeMapHighlightingMap } from '../context/TreeMapHighlightingMapContext.js';
import { useHighlightedState } from '../store/selectors.js';
import type { InternalTreeMapNode } from '../types/internal.js';
import {
  nodePadding,
  nodePaddingOuter,
  nodePaddingTop,
} from '../utils/node-padding.js';
import { withParentLabel } from '../utils/with-parent-label.js';
import { TreeMapCanvas } from './TreeMapCanvas.js';
import { TreeMapCluster } from './TreeMapCluster.js';

export const TreeMapInternal = forwardRef<HTMLDivElement, PropsWithChildren>(
  (props, forwardedRef): JSX.Element => {
    const { children } = props;
    const { width, height } = useGraphSize();
    const roundedWidth = Math.floor(width);
    const roundedHeight = Math.floor(height);
    const labelsDisplay = useLabelsDisplay();
    const { dataForRendering } = useTreeMapAggregationData();
    const highlightingMap = useTreeMapHighlightingMap();

    const hasParentLabels = withParentLabel(labelsDisplay);

    // build hierarchy with concrete coordinates and width/size of nodes based on tree structure
    const root = useMemo(
      () => hierarchy<InternalTreeMapNode>(dataForRendering),
      [dataForRendering],
    );

    const highlightedItem = useHighlightedState();

    const layout = treemap<InternalTreeMapNode>()
      .tile(treemapSquarify)
      .size([roundedWidth, roundedHeight])
      .padding((node) => nodePadding(node, hasParentLabels))
      .round(true);

    if (hasParentLabels) {
      layout
        .paddingOuter((node) => nodePaddingOuter(node))
        .paddingTop((node) => nodePaddingTop(node));
    }

    const rootNode = layout(root.sum((d) => (d.children ? 0 : d.value)));

    return (
      <div ref={forwardedRef} aria-label={TREEMAP_CHART_ARIA_LABEL} role="img">
        {children}
        <CanvasContext.Provider
          value={{
            shapeRefs: new Map<string, RefObject<CanvasShape>>(),
          }}
        >
          <Canvas width={roundedWidth} height={roundedHeight}>
            {hasParentLabels && (
              <TreeMapCluster
                treeMapData={rootNode}
                highlightedItem={highlightedItem}
                highlightingMap={highlightingMap}
              />
            )}
            <TreeMapCanvas
              treeMapData={rootNode}
              highlightedItem={highlightedItem}
              highlightingMap={highlightingMap}
            />
          </Canvas>
        </CanvasContext.Provider>
      </div>
    );
  },
);
